import type { InvoiceData, InvoiceDefaults, InvoicePhotoMetadata, InvoiceRecord } from "@shared/invoice";
import type { InvoiceLayout, InvoiceLayoutTemplate } from "@shared/invoice-layout";
import { fileToCompressedBase64 } from "./image-compress";

async function request<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method, credentials: "include",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Rechnung konnte nicht verarbeitet werden");
  }
  return response.json();
}

export const InvoiceApi = {
  layoutTemplates: () => request<InvoiceLayoutTemplate[]>("/api/invoice-layout-templates"),
  createLayoutTemplate: (name: string, layout: InvoiceLayout) =>
    request<InvoiceLayoutTemplate>("/api/invoice-layout-templates", "POST", { name, layout }),
  renameLayoutTemplate: (id: string, name: string) =>
    request<InvoiceLayoutTemplate>(`/api/invoice-layout-templates/${encodeURIComponent(id)}`, "PATCH", { name }),
  deleteLayoutTemplate: (id: string) =>
    request<{ ok: boolean }>(`/api/invoice-layout-templates/${encodeURIComponent(id)}`, "DELETE"),
  defaults: () => request<InvoiceDefaults>("/api/invoice-defaults"),
  saveDefaults: (defaults: InvoiceDefaults) => request<InvoiceDefaults>("/api/invoice-defaults", "PUT", defaults),
  list: (projectId: number | string) =>
    request<InvoiceRecord[]>(`/api/projects/${projectId}/invoices`),
  get: (id: number | string) => request<InvoiceRecord>(`/api/invoices/${id}`),
  create: (projectId: number | string, data: InvoiceData, photoIds: number[]) =>
    request<InvoiceRecord>(`/api/projects/${projectId}/invoices`, "POST", { data, photoIds }),
  save: (id: number | string, data: InvoiceData) =>
    request<InvoiceRecord>(`/api/invoices/${id}`, "PUT", { data }),
  photos: (id: number | string) =>
    request<InvoicePhotoMetadata[]>(`/api/invoices/${id}/photos`),
  photoUrl: (id: number | string, photoId: number) => `/api/invoices/${id}/photos/${photoId}/image`,
  async uploadPhoto(id: number | string, file: File): Promise<InvoicePhotoMetadata> {
    const compressed = await fileToCompressedBase64(file);
    return request(`/api/invoices/${id}/photos`, "POST", {
      fileName: file.name, displayName: file.name, description: "",
      mimeType: compressed.mimeType, fileSize: compressed.fileSize, data: compressed.data,
    });
  },
  updatePhoto: (id: number | string, photoId: number, patch: { displayName: string; description: string }) =>
    request<InvoicePhotoMetadata>(`/api/invoices/${id}/photos/${photoId}`, "PATCH", patch),
  deletePhoto: (id: number | string, photoId: number) =>
    request<{ ok: boolean }>(`/api/invoices/${id}/photos/${photoId}`, "DELETE"),
};
